import {useCallback, useState} from 'react'
import {useGetBillingData} from '../menu/useGetBillingData'
import {apiConstants, getGeneric} from '../common/constants'
import {convertToCSV, downloadCSV, formatDate} from '../helpers/UtilityHelper'
import {useGenericPostRequestQuery} from '../redux/commonRTK'

const headers = [
  {label: 'Transaction ID', key: 'transactionId'},
  {label: 'Credit Amount', key: 'credit'},
  {label: 'Debit Amount', key: 'debit'},
  {label: 'Source', key: 'transactionSource'},
  {label: 'Status', key: 'transactionStatus'},
  {label: 'Trans. Time', key: 'date'},
]

// Hold columns are blank on ordinary rows. A weight-discrepancy HOLD / RELEASE
// row moves no money (credit and debit both blank), so without these two the
// export would show an entry with no amount at all; CONVERTED marks the debit
// row that settled a hold.
const passbookHeaders = [
  {label: 'AWB Number', key: 'awbNumber'},
  {label: 'Credit Amount', key: 'credit'},
  {label: 'Debit Amount', key: 'debit'},
  {label: 'Hold Event', key: 'holdEvent'},
  {label: 'Hold Amount', key: 'holdAmount'},
  {label: 'Description', key: 'description'},
  {label: 'Ref. Number', key: 'orderReferenceNumber'},
  {label: 'Trans. Time', key: 'date'},
]

function useWalletRecharge(options) {
  const [isDownloading, setIsDownloading] = useState(false)
  const {dateRange, sizePerPage, page, isPassbook, awb} = options
  const {merchantId, walletDetails} = useGetBillingData()
  const skip = !merchantId || !walletDetails || !walletDetails?.walletType

  const startDate = formatDate(dateRange[0].startDate, 'yyyy-MM-dd')
  const endDate = formatDate(dateRange[0].endDate, 'yyyy-MM-dd')

  const {
    data: walletRecharge,
    isLoading: isLoadingWalletRecharge,
    isFetching: isFetchingWalletRecharge,
  } = useGenericPostRequestQuery(
    {
      type: 'GET',
      url: isPassbook ? apiConstants.ORDER_TRANSACTION : apiConstants.OTHER_TRANSACTION,
      data: {
        from_date: startDate,
        to_date: endDate,
        merchantId: merchantId,
        limit: sizePerPage,
        offset: page * sizePerPage,
        ...(awb && {awb}),
      },
    },
    {skip}
  )

  const downloadTransactionFile = useCallback(async () => {
    try {
      setIsDownloading(true)

      const url = isPassbook ? apiConstants.ORDER_TRANSACTION : apiConstants.OTHER_TRANSACTION
      const response = await getGeneric(url, {
        from_date: startDate,
        to_date: endDate,
        merchantId: merchantId,
        limit: 100000,
        ...(awb && {awb}),
      })
      const csvData = convertToCSV(response?.data || [], isPassbook ? passbookHeaders : headers)
      const filename = isPassbook ? 'order-transaction.csv' : 'other-transaction.csv'
      downloadCSV(csvData, filename)
    } catch (err) {
      console.error(err)
    } finally {
      setIsDownloading(false)
    }
  }, [startDate, endDate, merchantId, isPassbook, awb])

  return {
    isLoading: isLoadingWalletRecharge || isFetchingWalletRecharge,
    walletRecharge,
    downloadTransactionFile,
    isDownloading,
  }
}

export default useWalletRecharge
